
import React, { useState, useEffect } from "react"
import { ChevronLeft, Settings, Save, RefreshCw, Check } from 'lucide-react'
import { useNavigate, useParams } from 'react-router-dom'
import axiosInstance from "../utils/axiosInstance"
import { getToken } from "../utils/authStorage"
import { API_ENDPOINTS } from "../constants/config"
import {
  FEATURE_CATEGORIES,
  CATEGORY_LABELS,
  CATEGORY_ICONS,
  getCategorizedFeatures
} from "../constants/features"
import { useFeatureFlag } from "../hooks/useFeatureFlag"
import { Button } from "@/components/ui/button"
import { Switch } from "@/components/ui/switch"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Alert, AlertDescription } from "@/components/ui/alert"

// This product only sells Inventory - the superadmin school-settings UI shows
// only inventory-related feature toggles plus Staff and Settings. Other
// features keep their saved state untouched (fetch/save still handle the
// full feature map).
const VISIBLE_CATEGORIES = [
  FEATURE_CATEGORIES.INVENTORY,
  FEATURE_CATEGORIES.USERS,
  FEATURE_CATEGORIES.SETTINGS,
]

// Within a visible category, optionally show only some features
// (Users category holds students/teachers/staff - only staff applies here;
// Settings category is mostly school features - only About and Permissions apply).
const VISIBLE_FEATURES_BY_CATEGORY = {
  [FEATURE_CATEGORIES.USERS]: ['staff'],
  [FEATURE_CATEGORIES.SETTINGS]: ['about', 'permissions'],
}

const filterVisibleFeatures = (categorized) => {
  const filtered = {}
  Object.keys(categorized).forEach(category => {
    const allowedKeys = VISIBLE_FEATURES_BY_CATEGORY[category]
    filtered[category] = allowedKeys
      ? categorized[category].filter(f => allowedKeys.includes(f.key))
      : categorized[category]
  })
  return filtered
}

function SchoolSettingsPageNew() {
  const navigate = useNavigate()
  const { schoolId: schoolIdFromParams } = useParams()
  const { refreshFeatures } = useFeatureFlag()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [features, setFeatures] = useState({})
  const [categorizedFeatures, setCategorizedFeatures] = useState({})
  const [activeCategory, setActiveCategory] = useState(FEATURE_CATEGORIES.INVENTORY)
  const [saveSuccess, setSaveSuccess] = useState(false)
  const [error, setError] = useState(null)

  // Get school ID from params (SuperAdmin) or from logged-in user (SchoolAdmin)
  const getSchoolId = () => {
    if (schoolIdFromParams) return schoolIdFromParams
    
    try {
      const userStr = localStorage.getItem('user')
      if (!userStr) return null
      const user = JSON.parse(userStr)
      return user?.school?._id || user?.school || null
    } catch (error) {
      console.error('Error getting school ID:', error)
      return null
    }
  }

  const schoolId = getSchoolId()

  useEffect(() => {
    fetchSettings()
  }, [schoolId])

  const fetchSettings = async () => {
    if (!schoolId) {
      setError('Unable to determine school ID. Please try logging in again.')
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      setError(null)
      const token = getToken()
      
      // Fetch school settings
      const res = await axiosInstance.get(
        API_ENDPOINTS.schoolFeatures(schoolId),
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      )
      
      if (res.data && res.data.data) {
        console.log('Fetched settings:', res.data.data)
        const settingsData = res.data.data
        
        // Extract features from settings and ensure all expected features exist
        const featuresConfig = getCategorizedFeatures()
        const completeFeatures = {}
        
        // Initialize all expected features with default enabled: true
        Object.keys(featuresConfig).forEach(category => {
          featuresConfig[category].forEach(feature => {
            completeFeatures[feature.key] = { enabled: true }
          })
        })
        
        // Merge with API data (API values override defaults)
        if (settingsData.features && typeof settingsData.features === 'object') {
          Object.keys(settingsData.features).forEach(key => {
            if (completeFeatures[key]) {
              completeFeatures[key] = settingsData.features[key]
            }
          })
        }
        
        setFeatures(completeFeatures)
      }
      
      // Get categorized features structure (filtered to what this product shows)
      setCategorizedFeatures(filterVisibleFeatures(getCategorizedFeatures()))
    } catch (error) {
      console.error("Error fetching settings:", error)
      setError("Failed to load settings. Please try again.")
    } finally {
      setLoading(false)
    }
  }

  const handleFeatureToggle = (featureKey, enabled) => {
    setFeatures(prev => ({
      ...prev,
      [featureKey]: {
        ...prev[featureKey],
        enabled
      }
    }))
    setSaveSuccess(false)
  }

  const handleSave = async () => {
    if (!schoolId) {
      console.error("School ID is missing")
      return
    }
    
    try {
      setSaving(true)
      setError(null)
      const token = getToken()
      
      const payload = {
        features
      }
      
      console.log('Saving features:', payload)
      
      // Save to endpoint
      const res = await axiosInstance.patch(
        API_ENDPOINTS.schoolFeatures(schoolId),
        payload,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      )
      
      if (res.data.status === "success") {
        console.log('=== SETTINGS SAVE SUCCESS ===')
        console.log('[SETTINGS] Settings saved successfully')
        console.log('[SETTINGS] Saved features count:', Object.keys(features).length)
        console.log('[SETTINGS] Enabled features:', Object.entries(features)
          .filter(([_, v]) => v?.enabled === true)
          .map(([k]) => k))
        setSaveSuccess(true)
        
        // Refresh feature flags to update sidebar immediately
        try {
          console.log('[SETTINGS] Calling refreshFeatures()...')
          await refreshFeatures()
          console.log('[SETTINGS] ✓ Feature flags refreshed successfully')
          console.log('[SETTINGS] After refresh - check sidebar for updates')
        } catch (error) {
          console.error('[SETTINGS] ❌ Error refreshing feature flags:', error)
        }
        
        setTimeout(() => {
          setSaveSuccess(false)
        }, 3000)
      }
    } catch (error) {
      console.error("Error saving settings:", error)
      setError("Failed to save settings. Please try again.")
    } finally {
      setSaving(false)
    }
  }

  const getEnabledCount = (category) => {
    const categoryFeatures = categorizedFeatures[category] || []
    return categoryFeatures.filter(feature =>
      features[feature.key]?.enabled === true
    ).length
  }

  const getTotalCount = (category) => {
    return (categorizedFeatures[category] || []).length
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <RefreshCw className="w-8 h-8 text-blue-600 animate-spin" />
          <div className="text-gray-600">Loading settings...</div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                title="Go back"
              >
                <ChevronLeft className="w-6 h-6 text-gray-600" />
              </button>
              <div className="flex items-center gap-3">
                <Settings className="w-7 h-7 text-blue-600" />
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">School Settings</h1>
                  <p className="text-sm text-gray-500">Configure available features for this school</p>
                </div>
              </div>
            </div>
            
            <Button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2"
            >
              {saving ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Save Changes
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Success/Error Messages */}
      {saveSuccess && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
          <Alert className="bg-green-50 border-green-200">
            <Check className="h-4 w-4 text-green-600" />
            <AlertDescription className="text-green-800">
              Settings saved successfully!
            </AlertDescription>
          </Alert>
        </div>
      )}
      
      {error && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        </div>
      )}

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Tabs value={activeCategory} onValueChange={setActiveCategory} className="w-full">
          {/* Category Tabs */}
          <TabsList className="flex w-full flex-wrap justify-start gap-2 h-auto p-2 bg-white rounded-lg shadow-sm">
            {VISIBLE_CATEGORIES.map(category => {
              const enabled = getEnabledCount(category)
              const total = getTotalCount(category)
              
              return (
                <TabsTrigger
                  key={category}
                  value={category}
                  className="flex flex-col items-center gap-1 py-3 px-2 data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700"
                >
                  <span className="text-2xl">{CATEGORY_ICONS[category]}</span>
                  <span className="text-xs font-medium text-center line-clamp-2">
                    {CATEGORY_LABELS[category]}
                  </span>
                  <Badge 
                    variant={enabled === total ? "default" : "secondary"}
                    className="text-xs px-1.5 py-0"
                  >
                    {enabled}/{total}
                  </Badge>
                </TabsTrigger>
              )
            })}
          </TabsList>

          {/* Feature Cards for Each Category */}
          {VISIBLE_CATEGORIES.map(category => (
            <TabsContent key={category} value={category} className="mt-6">
              <Card>
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <span className="text-4xl">{CATEGORY_ICONS[category]}</span>
                    <div>
                      <CardTitle className="text-2xl">{CATEGORY_LABELS[category]}</CardTitle>
                      <CardDescription>
                        {getEnabledCount(category)} of {getTotalCount(category)} features enabled
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {(categorizedFeatures[category] || []).map(feature => {
                      const isEnabled = features[feature.key]?.enabled ?? true
                      
                      return (
                        <Card 
                          key={feature.key} 
                          className={`transition-all ${
                            isEnabled 
                              ? 'border-blue-200 bg-blue-50/30' 
                              : 'border-gray-200 bg-gray-50/30'
                          }`}
                        >
                          <CardContent className="pt-6">
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex-1 min-w-0">
                                <h3 className="font-semibold text-gray-900 mb-1 truncate">
                                  {feature.name}
                                </h3>
                                <p className="text-sm text-gray-600 line-clamp-2">
                                  {feature.description}
                                </p>
                              </div>
                              <Switch
                                checked={isEnabled}
                                onCheckedChange={(checked) => 
                                  handleFeatureToggle(feature.key, checked)
                                }
                                className="flex-shrink-0"
                              />
                            </div>
                            <div className="mt-3 pt-3 border-t">
                              <Badge 
                                variant={isEnabled ? "default" : "secondary"}
                                className="text-xs"
                              >
                                {isEnabled ? 'Enabled' : 'Disabled'}
                              </Badge>
                            </div>
                          </CardContent>
                        </Card>
                      )
                    })}
                  </div>
                  
                  {(!categorizedFeatures[category] || categorizedFeatures[category].length === 0) && (
                    <div className="text-center py-12 text-gray-500">
                      No features available in this category
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </div>
  )
}

export default SchoolSettingsPageNew
